import React, { useEffect, useState } from "react";
import { Button, Form, Card, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faCheckCircle,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { InvisilatorService } from "Services/InvisilatorService";
import { toast } from "react-toastify";
import TablePagination from "components/Pagination/Table.paginaition";

export default function AssignStudentsPage() {
  const navigate = useNavigate();
  const { invigilatorId } = useParams();
  const location = useLocation();

  const invigilatorInfo = location.state as {
    name?: string;
    email?: string;
  };

  const [students, setStudents] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  /* FETCH STUDENTS */
  const fetchStudents = async () => {
    try {
      const res = await InvisilatorService.getAllUsers(currentPage, perPage,search);

      if (res?.status === 200) {
        const list = res.data?.data?.users || [];
        const totalCount = res.data?.data?.count || 0;
        setStudents(list);
        setTotal(totalCount);
      }
    } catch (err) {
      toast.error("Failed to load students");
    }
  };

  /* FETCH ASSIGNED STUDENTS */
  const fetchAssignedStudents = async () => {
    try {
      if (!invigilatorId) return;

      const res = await InvisilatorService.getAssignedStudents(invigilatorId);

      if (res?.status === 200) {
        const assigned =
          res.data?.data?.invigilator?.assignedStudents?.map(
            (s: any) => s._id,
          ) || [];

        setSelected(assigned);
      }
    } catch (err) {
      console.log("Assigned students fetch error");
    }
  };

  useEffect(() => {
    fetchStudents();
  }, [currentPage, perPage,search]);

  useEffect(() => {
    fetchAssignedStudents();
  }, [invigilatorId]);

  const filteredStudents = students.filter(
    (s) =>
      s.name?.toLowerCase().includes(search.toLowerCase()) ||
      s.email?.toLowerCase().includes(search.toLowerCase()),
  );

  const toggleStudent = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  };

  const isAllSelected =
    filteredStudents.length > 0 &&
    filteredStudents.every((s) => selected.includes(s._id));

  const handleSelectAll = () => {
    const pageIds = filteredStudents.map((s) => s._id);

    if (isAllSelected) {
      setSelected((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelected((prev) => {
        const newSelected = [...prev];
        pageIds.forEach((id) => {
          if (!newSelected.includes(id)) newSelected.push(id);
        });
        return newSelected;
      });
    }
  };

  /* ASSIGN */
  const handleAssign = async () => {
    try {
      const payload = {
        students: selected,
      };

      const res = await InvisilatorService.assignStudents(
        invigilatorId!,
        payload,
      );

      if (res?.status === 200) {
        toast.success("Students assigned successfully");
        navigate(-1);
      }
    } catch (err) {
      toast.error("Failed to assign students");
    }
  };

  return (
    <div className="p-4">
      <div className="d-flex align-items-center mb-4">
        <Button
          variant="light"
          className="me-3 border"
          onClick={() => navigate(-1)}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </Button>

        <h4 className="mb-0 fw-bold">
          <FontAwesomeIcon icon={faUsers} className="me-2 text-primary" />
          Assign Students
        </h4>
      </div>

      <Row>
        <Col lg={8} className="mx-auto">
          <Card className="mb-3 shadow-sm border-0">
            <Card.Body>
              <div className="fw-semibold text-muted mb-1">
                Assigning students to
              </div>

              <div className="fs-5 fw-bold">
                {invigilatorInfo?.name || "Unknown Invigilator"}
              </div>

              <div className="text-muted">{invigilatorInfo?.email || ""}</div>
            </Card.Body>
          </Card>

          {/* SEARCH */}
          <Card className="mb-3 shadow-sm border-0">
            <Card.Body>
              <Form.Control
                placeholder="Search student by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Card.Body>
          </Card>

          <Card className="shadow-sm border-0">
            <Card.Body className="p-0">
              {filteredStudents.length > 0 && (
                <div className="px-3 py-2 border-bottom bg-light d-flex justify-content-between align-items-center">
                  <strong>Select All (Current Page)</strong>
                  <Form.Check
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                  />
                </div>
              )}

              {filteredStudents.length === 0 && (
                <div className="p-4 text-center text-muted">
                  No students found
                </div>
              )}

              {filteredStudents.map((s) => (
                <div
                  key={s._id}
                  className="d-flex justify-content-between align-items-center px-3 py-3 border-bottom"
                >
                  <div>
                    <div className="fw-semibold">{s.name}</div>
                    <small className="text-muted">{s.email}</small>
                  </div>

                  <Form.Check
                    type="checkbox"
                    checked={selected.includes(s._id)}
                    onChange={() => toggleStudent(s._id)}
                  />
                </div>
              ))}
            </Card.Body>
          </Card>

          <TablePagination
            total={total}
            currentPage={currentPage}
            perPage={perPage}
            handlePageChange={(page: number) => setCurrentPage(page)}
            setPerPage={(size: number) => {
              setPerPage(size);
              setCurrentPage(1);
            }}
          />

          {/* FOOTER */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-muted">
              <FontAwesomeIcon
                icon={faCheckCircle}
                className="me-1 text-success"
              />
              Selected: <strong>{selected.length}</strong>
            </div>

            <Button
              variant="primary"
              disabled={selected.length === 0}
              onClick={handleAssign}
            >
              Assign Students
            </Button>
          </div>
        </Col>
      </Row>
    </div>
  );
}
